import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarDays, Plus, Users, Video } from "lucide-react";

import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminSeminars } from "@/hooks/useAdminData";

export const Route = createFileRoute("/admin/sessions/")({
  component: AdminSessions,
});

function AdminSessions() {
  const { data: seminars = [], isLoading } = useAdminSeminars();
  const upcoming = seminars.filter((s: any) => s.status === "upcoming");
  const totalAttendees = seminars.reduce((sum: number, s: any) => sum + (s.attendees || 0), 0);

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Training sessions"
        subtitle="Coordinate live training, orientation, and placement support sessions from one operational view."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Sessions" }]}
        action={
          <Button asChild>
            <Link to={"/admin/seminars" as any}>
              <Plus />
              Schedule session
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Upcoming sessions" value={upcoming.length} icon={<CalendarDays />} />
        <StatCard label="Total attendees" value={totalAttendees} icon={<Users />} />
        <StatCard label="Total seminars" value={seminars.length} icon={<Video />} />
      </div>

      <SectionCard title="Session schedule" noPadding>
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : seminars.length === 0 ? (
          <EmptyState
            bare
            icon={<CalendarDays />}
            title="No sessions scheduled"
            description="Use seminars to create and manage upcoming training events."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Session</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>Date &amp; time</TableHead>
                <TableHead className="text-right">Attendees</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {seminars.map((s: any) => (
                <TableRow key={s.id} className="h-11 [&>td]:py-1.5">
                  <TableCell className="min-w-[200px] pl-4 font-medium text-foreground md:pl-5">
                    {s.title}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-body">{s.host}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {s.date} · {s.time}
                  </TableCell>
                  <TableCell className="text-right font-mono text-foreground tabular-nums">
                    {s.attendees}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={s.status} />
                  </TableCell>
                  <TableCell className="pr-4 text-right md:pr-5">
                    <Badge variant="outline" className="uppercase">
                      {s.type}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
